// Adding your own devices (M41, docs/adding-devices.md).
import {
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import type { QrDeviceKind } from '@twinden/shared';
import { api, ApiError, type Device } from './api';
import { t } from '../i18n';

/** What kind of device this browser runs on, from its user agent. */
export function guessDeviceKind(
  ua: string = navigator.userAgent
): QrDeviceKind {
  if (/iPhone/i.test(ua)) return 'iphone';
  if (/iPad/i.test(ua)) return 'ipad';
  if (/Android/i.test(ua)) return 'android';
  if (/Macintosh/i.test(ua)) return 'mac';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Linux/i.test(ua)) return 'linux';
  return 'other';
}

/** A sensible default name for the passkey this browser creates. */
export function guessDeviceName(): string {
  const kind = guessDeviceKind();
  return kind === 'other' ? '' : t(`account.devices.${kind}`);
}

/** A fresh passkey check that the person holding this device is you (D91). */
export async function confirmItsYou() {
  const options = await api.verifyStart();
  return startAuthentication({
    optionsJSON:
      options.data as unknown as PublicKeyCredentialRequestOptionsJSON,
  });
}

/** A one-time link that adds a device to your account (D90, D92). */
export async function makeDeviceLink(familyId?: string | null) {
  const verification = await confirmItsYou();
  const response = await api.createDeviceLink(verification, familyId);
  return response.data;
}

/**
 * Creates a passkey on this device (D93). Right after a passkey sign-in no
 * check is needed; otherwise the server asks for one and we run it.
 * `another` makes one even if this device already has one, so it can be
 * saved somewhere else (a password manager such as NordPass).
 */
export async function addPasskeyHere(
  deviceName?: string,
  another = false
): Promise<Device> {
  let options;
  try {
    options = await api.addPasskeyStart(undefined, another);
  } catch (err) {
    if (!(err instanceof ApiError && err.code === 'REAUTH_REQUIRED')) throw err;
    options = await api.addPasskeyStart(await confirmItsYou(), another);
  }
  const attestation = await startRegistration({
    optionsJSON:
      options.data as unknown as PublicKeyCredentialCreationOptionsJSON,
  });
  const response = await api.addPasskeyFinish(
    attestation,
    deviceName || undefined
  );
  return response.data;
}

/** The device refused because it already has a passkey for this account. */
export function alreadyHasPasskey(err: unknown): boolean {
  return err instanceof Error && err.name === 'InvalidStateError';
}

/** Friendly words for what can go wrong in these ceremonies. */
export function deviceErrorMessage(err: unknown): string | null {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) {
    // Dismissed the passkey prompt: not an error worth showing.
    if (err.name === 'NotAllowedError' || err.name === 'AbortError') {
      return null;
    }
    if (err.name === 'InvalidStateError') {
      return t('account.devices.alreadyHasPasskey');
    }
  }
  return t('common.somethingWrong');
}
